import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { whyChooseUs } from '../data/mock';

const WhyUs = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-maritime-primary">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1595587637401-83ff822bd63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxoYXJib3J8ZW58MHx8fHwxNzcxMjAyMTk1fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-white mb-6">Why Choose Gamma Marine</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Experience the difference of working with an approved national inspection agency 
              committed to excellence, accuracy, and reliability.
            </p>
          </div>
        </div>
      </section>

      {/* Main Differentiators */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maritime-primary mb-4">
              Your Trusted Maritime Partner
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine official accreditation, certified expertise, and unwavering commitment 
              to deliver marine survey services you can depend on.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((feature) => {
              const IconComponent = require('lucide-react')[feature.icon];
              return (
                <Card
                  key={feature.id}
                  className="group border-2 border-transparent hover:border-maritime-accent/30 hover:shadow-2xl transition-all duration-300"
                >
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-maritime-accent/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-maritime-accent/20 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-maritime-accent" />
                    </div>
                    <h3 className="text-xl font-bold text-maritime-primary mb-3 group-hover:text-maritime-accent transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-20 bg-gradient-to-b from-maritime-primary/5 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-maritime-primary mb-4">
                Our Credentials & Accreditations
              </h2>
              <p className="text-lg text-gray-600">
                Official recognition and certifications that guarantee quality service
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Maritime NZ Approval */}
              <Card className="border-4 border-maritime-accent/20 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-maritime-accent rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-maritime-primary mb-2">
                        Approved National Inspection Agency
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-3">
                        Officially approved by Maritime New Zealand, the regulatory authority 
                        for maritime safety in New Zealand waters.
                      </p>
                      <div className="inline-block bg-maritime-accent/10 text-maritime-accent text-sm font-semibold px-4 py-2 rounded-full">
                        Maritime New Zealand Certified
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Certifications */}
              <Card className="border-2 border-maritime-accent/20 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-maritime-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-8 h-8 text-maritime-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-maritime-primary mb-2">
                        Experienced Marine Surveyors
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-3">
                        Our surveyors bring decades of combined practical experience in maritime operations, 
                        vessel management, and marine engineering, providing real-world insights and 
                        comprehensive assessment capabilities.
                      </p>
                      <div className="inline-block bg-maritime-primary/10 text-maritime-primary text-sm font-semibold px-4 py-2 rounded-full">
                        Professionally Qualified
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Global Standards */}
              <Card className="border-2 border-maritime-accent/20 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-maritime-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-8 h-8 text-maritime-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-maritime-primary mb-2">
                        International Compliance
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-3">
                        Adherence to global maritime compliance standards including IMO 
                        regulations and international best practices.
                      </p>
                      <div className="inline-block bg-maritime-primary/10 text-maritime-primary text-sm font-semibold px-4 py-2 rounded-full">
                        Global Standards
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Training Partnership */}
              <Card className="border-2 border-maritime-accent/20 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-maritime-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-8 h-8 text-maritime-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-maritime-primary mb-2">
                        Training Institute Partnership
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-3">
                        Collaboration with City of Glasgow College (UK) demonstrates our 
                        commitment to maritime education and industry development.
                      </p>
                      <div className="inline-block bg-maritime-accent/10 text-maritime-accent text-sm font-semibold px-4 py-2 rounded-full">
                        Education Partner
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Promise */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-maritime-primary mb-4">
                Our Service Promise
              </h2>
              <p className="text-lg text-gray-600">
                What you can expect when you work with Gamma Marine
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  title: 'Accuracy & Independence',
                  description: 'Unbiased, thorough assessments with detailed documentation and evidence-based conclusions.'
                },
                {
                  title: 'Timely Response',
                  description: 'Quick mobilization and efficient execution to meet your urgent requirements and minimize operational delays.'
                },
                {
                  title: 'Transparent Reporting',
                  description: 'Clear, comprehensive reports with photographic evidence and actionable recommendations.'
                },
                {
                  title: 'Professional Excellence',
                  description: 'Consistently high-quality service delivered by qualified experts with extensive industry experience.'
                },
                {
                  title: 'Regional Expertise',
                  description: 'Deep understanding of local maritime operations and regulations across Australia, New Zealand, and Pacific Islands.'
                }
              ].map((promise, index) => (
                <Card key={index} className="border-l-4 border-maritime-accent hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-maritime-primary mb-2">{promise.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{promise.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-maritime-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              Experience the Gamma Marine Difference
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Partner with New Zealand's approved national inspection agency for 
              reliable, professional marine survey services.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-maritime-accent hover:bg-maritime-accent/90 text-white"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
